import axios from 'axios';

import { formatRefId } from 'root/apps/QuoteMachineIntegration/utils/referenceHelpers';

export const BLOCKING_REASON_TYPES = {
  INVOICE_HAS_PAYMENTS: 'invoice-has-payments',
  CREDIT_MEMO_WAS_APPLIED: 'credit-memo-was-applied',
  INVOICE_HAS_PROCESSING_BALANCE: 'invoice-has-processing-balance',
} as const;

export interface BlockingTransaction {
  transactionRef: string;
  reason: string;
  message: string;
  documentIdentifier?: string;
}

export interface TransactionVoidableStateResponse {
  voidable: boolean;
  blockingTransactions?: BlockingTransaction[];
}

export interface VoidValidationResult {
  isAllowed: boolean;
  errorMessage: string;
}

export default class VoidService {
  /**
   * Checks if a transaction can be voided via the invoicing API
   * @param saleID - The sale ID to check
   * @param creditAccountId - The credit account ID associated with the sale
   * @returns VoidValidationResult indicating if void is allowed and any error message
   */
  static async checkInvoicingAllowsVoid(
    saleID: string | number,
    creditAccountId?: string | number | null
  ): Promise<VoidValidationResult> {
    // If no credit account, allow void (let other validation rules apply)
    if (!creditAccountId) {
      return {
        isAllowed: true,
        errorMessage: '',
      };
    }

    try {
      // Format IDs with ls-retail prefix for API call
      const formattedCreditAccountId = formatRefId(String(creditAccountId));
      const formattedSaleId = formatRefId(String(saleID));

      const response = await axios.get(
        `/admin/invoicing/v1/credit-accounts/${formattedCreditAccountId}/transactions/${formattedSaleId}/voidable/`
      );

      // 200 OK - Check voidable field
      if (response.status === 200) {
        const { voidable, blockingTransactions } =
          response.data as TransactionVoidableStateResponse;

        if (voidable) {
          return {
            isAllowed: true,
            errorMessage: '',
          };
        }

        // Block void - generate appropriate error message
        const errorMessage = this.generateErrorMessage(blockingTransactions || []);
        return {
          isAllowed: false,
          errorMessage,
        };
      }

      // Unexpected status code
      return {
        isAllowed: false,
        errorMessage:
          'Unable to verify void eligibility. Please check your connection and try again.',
      };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        const status = error.response.status;

        if (status === 404) {
          // 404: Transaction unknown to invoicing API - allow void (let other validation rules apply)
          return {
            isAllowed: true,
            errorMessage: '',
          };
        }
        if (status === 409) {
          // 409: Transaction is pending - block void
          return {
            isAllowed: false,
            errorMessage: 'Cannot void: Transaction is still in pending state',
          };
        }
      }

      // Network/timeout error or other errors
      return {
        isAllowed: false,
        errorMessage:
          'Unable to verify void eligibility. Please check your connection and try again.',
      };
    }
  }

  /**
   * Creates a void intent for a transaction with credit account sale payments
   * @param saleId - The sale ID to void
   * @param creditAccountId - The credit account ID associated with the sale
   * @param amount - The total amount of credit account sale payments
   * @param currency - The currency code
   * @returns The response from the endpoint
   */
  static createVoidIntent(
    saleId: string | number,
    creditAccountId: string | number,
    amount: string,
    currency: string
  ) {
    const formattedCreditAccountId = formatRefId(String(creditAccountId));
    const formattedSaleId = formatRefId(String(saleId));

    return axios.put(
      `/admin/invoicing/v1/credit-accounts/${formattedCreditAccountId}/transactions/${formattedSaleId}/void-intent`,
      { amount, currency }
    );
  }

  /**
   * Generates a user-facing error message based on blocking transactions
   * @param blockingTransactions - Array of blocking transactions
   * @returns Formatted error message
   */
  private static generateErrorMessage(blockingTransactions: BlockingTransaction[]): string {
    if (!blockingTransactions.length) {
      return 'Cannot void: This transaction cannot be voided.';
    }

    // Group blocking transactions by reason
    const groupedByReason = blockingTransactions.reduce(
      (acc: Record<string, string[]>, transaction: BlockingTransaction) => {
        const reason = transaction.reason;
        const transactionRef = transaction.transactionRef.replace(/^ls-retail_/, '');

        if (!acc[reason]) {
          acc[reason] = [];
        }
        acc[reason].push(transactionRef);

        return acc;
      },
      {}
    );

    // Collect all error messages
    const errorMessages: string[] = [];

    // Check for invoice has payments
    if (groupedByReason[BLOCKING_REASON_TYPES.INVOICE_HAS_PAYMENTS]) {
      const transactionIds = groupedByReason[BLOCKING_REASON_TYPES.INVOICE_HAS_PAYMENTS];
      if (transactionIds.length > 0) {
        const saleIdsStr = transactionIds.join(', ');
        errorMessages.push(
          `Invoice from this sale has been paid by another transaction(s): ${saleIdsStr}`
        );
      } else {
        errorMessages.push('Invoice from this sale has been paid by another transaction.');
      }
    }

    // Check for credit memo was applied
    if (groupedByReason[BLOCKING_REASON_TYPES.CREDIT_MEMO_WAS_APPLIED]) {
      const transactionIds = groupedByReason[BLOCKING_REASON_TYPES.CREDIT_MEMO_WAS_APPLIED];
      if (transactionIds.length > 0) {
        const saleIdsStr = transactionIds.join(', ');
        errorMessages.push(
          `Credit memo from this sale has been applied to another transaction(s): ${saleIdsStr}`
        );
      } else {
        errorMessages.push('Credit memo from this sale has been applied to another transaction.');
      }
    }

    // Check for invoice has processing balance — payment still in-flight on the
    // sale's invoice. Block the void until payment settles.
    if (groupedByReason[BLOCKING_REASON_TYPES.INVOICE_HAS_PROCESSING_BALANCE]) {
      const documentIdentifier = blockingTransactions
        .filter((t) => t.reason === BLOCKING_REASON_TYPES.INVOICE_HAS_PROCESSING_BALANCE)
        .find((t) => !!t.documentIdentifier)?.documentIdentifier;
      if (documentIdentifier) {
        errorMessages.push(
          `This on-account sale has an associated invoice with a payment still processing. Try again once the payment is completed for "${documentIdentifier}"`
        );
      } else {
        errorMessages.push(
          'This on-account sale has an associated invoice with a payment still processing. Try again once the payment is completed.'
        );
      }
    }

    // Check for any unknown blocking reasons
    const knownReasons = Object.values(BLOCKING_REASON_TYPES);
    const unknownReasons = Object.keys(groupedByReason).filter(
      (reason) => !knownReasons.includes(reason as any)
    );

    unknownReasons.forEach((reason) => {
      // Try to use the message from the first transaction with this reason
      const transactionWithReason = blockingTransactions.find((t) => t.reason === reason);
      if (transactionWithReason?.message) {
        errorMessages.push(transactionWithReason.message);
      } else {
        const saleIdsStr = groupedByReason[reason].join(', ');
        errorMessages.push(`Blocked by transaction(s): ${saleIdsStr}`);
      }
    });

    // If we have error messages, combine them
    if (errorMessages.length > 0) {
      if (errorMessages.length === 1) {
        return `Cannot void: ${errorMessages[0]}`;
      }
      // Multiple reasons - format as a list
      return `Cannot void due to multiple reasons:\n• ${errorMessages.join('\n• ')}`;
    }

    // Fallback if somehow no messages were generated
    return 'Cannot void: This transaction cannot be voided.';
  }
}
